import { WalletStatus } from "@wallet-manager/node-types/dist/src/postgres/const";
import { isNil } from "lodash";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { getSpenderAddress } from "src/api/merchant";
import { useChains } from "src/components/FetchConfig";

export type SpenderAddress = {
  id: string;
  merchant_id: string;
  chain_type: number;
  chain_id: string;
  asset_name: string;
  spender_address: string;
  spender_contract_version: string;
  spender_contract_id: string;
  status: number;
  created_date: string;
  last_modified_date: string;
};

function useQuerySpenderAddress({
  chainName,
  assetName,
}: {
  chainName: string;
  assetName: string;
}) {
  const { selectChainByPieces } = useChains();
  const spenderAddressesRes = useQuery({
    queryKey: ["batchAutoSweepSpenderAddress", chainName, assetName],
    queryFn: () => {
      const { chain_id: chainId, chain_type: chainType } = selectChainByPieces({
        chain_name: chainName,
      });

      if (isNil(chainId) || isNil(chainType) || !assetName) {
        return Promise.resolve([]);
      }

      return getSpenderAddress({
        params: { chainId, chainType, assetName, status: WalletStatus.Enabled },
      });
    },
    staleTime: 60 * 60 * 12,
  });

  const spenderAddressesSelectOptions: Record<string, string> = useMemo(() => {
    if (!spenderAddressesRes.data) {
      return {};
    }

    return spenderAddressesRes.data.reduce(
      (acc: Record<string, string>, { spender_address }) => ({
        ...acc,
        [spender_address]: spender_address,
      }),
      {},
    );
  }, [spenderAddressesRes.data]);

  return { spenderAddressesSelectOptions };
}

export default useQuerySpenderAddress;
